import { Plugin, Widget, toWidget } from "ckeditor5";
import {
  CHIP_ATTRIBUTE,
  unwrapTokens,
  wrapTokens,
} from "src/components/CKEditor5/PlaceholderChip/placeholderChipMarkup.ts";
import {
  INSERT_PLACEHOLDER_CHIP,
  PLACEHOLDER_CHIP_MODEL,
} from "src/components/CKEditor5/PlaceholderChip/placeholderChipModel.ts";
import { InsertPlaceholderChipCommand } from "src/components/CKEditor5/PlaceholderChip/InsertPlaceholderChipCommand.ts";
import { placeholderRanges } from "src/lib/placeholders/placeholderTokens.ts";
import type { Element as ModelElement, Writer } from "ckeditor5";

/** Человеческое имя для токена; всё остальное редактору не нужно. */
export type PlaceholderChipItem = {
  value: string;
  label: string;
};

declare module "ckeditor5" {
  interface EditorConfig {
    /** Подписи для чипов: тот же список, что показывает панель плейсхолдеров. */
    placeholderChip?: {
      items: PlaceholderChipItem[];
    };
  }
}

/**
 * Плейсхолдер в редакторе — неделимый предмет, а не текст.
 *
 * До этого вставленный `{{ contact.EMPF_PRODUKT }}` был обычными символами:
 * курсор заходил внутрь, стёртая скобка ломала подстановку, а выглядело это
 * ровно как рабочий плейсхолдер. Узнать о поломке можно было лишь по красной
 * строке в панели сбоку — то есть после того, как её уже допустили.
 *
 * Теперь токен выделяется, копируется и удаляется целиком, и напечатать
 * что-либо внутри него нельзя.
 *
 * **В базу по-прежнему уходит `{{ token }}` и ничего больше.** Обёртка живёт
 * только внутри редактора: на загрузке она надевается, на выгрузке снимается.
 * Поэтому Brevo, Twig, проверка неисполнимых плейсхолдеров и подмена кодов
 * купонов в тестовом письме продолжают работать с тем же текстом, что и
 * раньше, а письма, написанные до появления чипов, открываются чипами.
 */
export class PlaceholderChip extends Plugin {
  public static get requires() {
    return [Widget] as const;
  }

  public static get pluginName() {
    return "PlaceholderChip" as const;
  }

  public init(): void {
    this.setItems(
      (this.editor.config.get("placeholderChip.items") as
        | PlaceholderChipItem[]
        | undefined) ?? [],
    );
    this.defineSchema();
    this.defineConverters();
    this.keepStoredTextPlain();
    this.convertWrittenTokens();

    this.editor.commands.add(
      INSERT_PLACEHOLDER_CHIP,
      new InsertPlaceholderChipCommand(this.editor),
    );
  }

  /**
   * Подписи приходят с сервера и к моменту создания редактора обычно ещё не
   * пришли.
   *
   * Из-за этого чипы получались с внутренними именами — `contact.VORNAME`
   * вместо «Vorname», — и заметить это на пустой форме нельзя: список
   * плейсхолдеров над полем уже отрисован, а редактор держит тот, что был при
   * запуске. Поэтому подписи не читаются из конфигурации намертво, а
   * обновляются, когда данные доехали.
   */
  private labels = new Map<string, string>();

  public setItems(items: PlaceholderChipItem[]): void {
    this.labels = new Map(items.map((item) => [item.value, item.label]));

    this.redrawChips();
  }

  private items(): Map<string, string> {
    return this.labels;
  }

  /** Перерисовывает уже стоящие чипы — иначе новые подписи увидит только текст,
   * вставленный после них. */
  private redrawChips(): void {
    const root = this.editor.model.document.getRoot();

    if (root === null) {
      return;
    }

    this.editor.editing.view.change(() => {
      for (const { item } of this.editor.model.createRangeIn(root)) {
        if (item.is("element", PLACEHOLDER_CHIP_MODEL)) {
          this.editor.editing.reconvertItem(item);
        }
      }
    });
  }

  private defineSchema(): void {
    this.editor.model.schema.register(PLACEHOLDER_CHIP_MODEL, {
      inheritAllFrom: "$inlineObject",
      allowAttributes: ["token"],
    });
  }

  private defineConverters(): void {
    const conversion = this.editor.conversion;

    conversion.for("upcast").elementToElement({
      view: {
        name: "span",
        attributes: { [CHIP_ATTRIBUTE]: true },
      },
      model: (viewElement, { writer }) =>
        writer.createElement(PLACEHOLDER_CHIP_MODEL, {
          token: viewElement.getAttribute(CHIP_ATTRIBUTE),
        }),
    });

    conversion.for("dataDowncast").elementToElement({
      model: PLACEHOLDER_CHIP_MODEL,
      view: (modelElement, { writer }) => {
        const token = String(modelElement.getAttribute("token") ?? "");

        const span = writer.createContainerElement("span", {
          [CHIP_ATTRIBUTE]: token,
        });

        writer.insert(
          writer.createPositionAt(span, 0),
          writer.createText(`{{ ${token} }}`),
        );

        return span;
      },
    });

    conversion.for("editingDowncast").elementToElement({
      model: PLACEHOLDER_CHIP_MODEL,
      view: (modelElement, { writer }) => {
        const token = String(modelElement.getAttribute("token") ?? "");
        const label = this.items().get(token) ?? token;

        const span = writer.createContainerElement("span", {
          class: "ck-placeholder-chip",
          [CHIP_ATTRIBUTE]: token,
          title: token,
        });

        writer.insert(
          writer.createPositionAt(span, 0),
          writer.createText(label),
        );

        // Ради этой строки всё и затевалось: виджет выделяется и удаляется
        // целиком, а курсор внутрь не заходит.
        return toWidget(span, writer, { label });
      },
    });
  }

  /**
   * Обёртка надевается на загрузке и снимается на выгрузке.
   *
   * Так хранимый текст остаётся прежним. Порядок важен: разбор HTML идёт
   * позже, поэтому подменяем строку до него (`high`), а результат выгрузки —
   * после того, как его собрали (`low`).
   *
   * Событий загрузки два, и это не придирка: первый текст редактор принимает
   * через `init`, а `set` вызывается только при последующей подмене значения
   * снаружи. Форма отдаёт письмо именно первым способом — с одним лишь `set`
   * чипы не появлялись вовсе, пока текст не трогали заново.
   */
  private keepStoredTextPlain(): void {
    const data = this.editor.data;

    for (const event of ["init", "set"]) {
      data.on(
        event,
        (_event, args) => {
          if (typeof args[0] === "string") {
            args[0] = wrapTokens(args[0]);
          }
        },
        { priority: "high" },
      );
    }

    data.on(
      "get",
      (event) => {
        if (typeof event.return === "string") {
          event.return = unwrapTokens(event.return);
        }
      },
      { priority: "low" },
    );
  }

  /**
   * Дописанный руками или вставленный из буфера токен тоже становится чипом.
   *
   * Иначе поведение зависит от того, как плейсхолдер попал в текст: кнопка
   * даёт чип, набор с клавиатуры и вставка из другого письма — обычные буквы,
   * которые ломаются посимвольно. Разница ничем не объяснима и заметна только
   * постфактум.
   *
   * Сделано пост-фиксером, а не слежением за клавишами: через него проходит
   * любое изменение модели, откуда бы текст ни взялся.
   */
  private convertWrittenTokens(): void {
    const model = this.editor.model;

    model.document.registerPostFixer((writer) => {
      const parents = new Set<ModelElement>();

      for (const change of model.document.differ.getChanges()) {
        if (change.type !== "insert") {
          continue;
        }

        const parent = change.position.parent;

        if (parent.is("element")) {
          parents.add(parent);
        }
      }

      let changed = false;

      for (const parent of parents) {
        changed = this.replaceTokensIn(parent, writer) || changed;
      }

      return changed;
    });
  }

  /**
   * @returns было ли что-то заменено — пост-фиксер по этому решает, повторять
   * ли проход.
   */
  private replaceTokensIn(parent: ModelElement, writer: Writer): boolean {
    if (!this.editor.model.schema.checkChild(parent, PLACEHOLDER_CHIP_MODEL)) {
      return false;
    }

    // Каждый не-текстовый ребёнок занимает в модели одну позицию, поэтому в
    // строке он тоже должен занимать один символ — иначе смещения разъедутся.
    // Символ выбран такой, который в токен попасть не может.
    const text = [...parent.getChildren()]
      .map((child) => (child.is("$text") ? child.data : "\u0000"))
      .join("");

    const [range] = placeholderRanges(text);

    if (range === undefined) {
      return false;
    }

    writer.remove(
      writer.createRange(
        writer.createPositionAt(parent, range.start),
        writer.createPositionAt(parent, range.end),
      ),
    );

    const chip = writer.createElement(PLACEHOLDER_CHIP_MODEL, {
      token: range.token,
    });

    writer.insert(chip, parent, range.start);
    writer.setSelection(writer.createPositionAfter(chip));

    return true;
  }
}
